import {
  filterRecordsByStaffCustomerAccess,
  getAssignedStaffNodeCodes,
  getNodeCustomerAccess,
  normalizeAccessValue,
  resolveNodeCode,
} from "../../../utils/staffCustomerAccess";

const MANAGING_FOLLOWUP_ROLES = [
  "super_admin",
  "admin",
  "sale",
  "accountant",
  "sleep_physician",
];

export const getCpapFollowupRoles = (currentUser) =>
  currentUser && Array.isArray(currentUser.roles) ? currentUser.roles : [];

export const isCpapFollowupNodeCustomerScopedUser = (currentUser) => {
  const roles = getCpapFollowupRoles(currentUser);
  return !roles.includes("super_admin") && roles.some((role) => ["admin", "staff"].includes(role));
};

export const getCpapFollowupAccessKey = (currentUser) => JSON.stringify([
  currentUser && currentUser.uid,
  currentUser && currentUser.displayName,
  getCpapFollowupRoles(currentUser),
]);

export const hasValidCpapCustomerAccess = (adminData) => Boolean(adminData && (
  !Object.prototype.hasOwnProperty.call(adminData, "customerAccess") ||
  (adminData.customerAccess && typeof adminData.customerAccess === "object" && !Array.isArray(adminData.customerAccess))
));

export const getCpapFollowupRecordValue = (record, key) => {
  if (record && record[key] !== undefined && record[key] !== null && record[key] !== "") return record[key];
  return (record && record.extendedProps && record.extendedProps[key]) || "";
};

const resolveCustomerId = (value, node) => {
  const key = normalizeAccessValue(value);
  const customers = (node && node.customers) || [];
  const matches = customers.filter((customer) => [
    customer.id, customer.CustomerNameEN, customer.CustomerNameTH, customer.customerName, customer.name,
  ].some((name) => normalizeAccessValue(name) === key));
  // An ambiguous display name must never grant access to another customer.
  return matches.length === 1 ? matches[0].id : matches.length > 1 ? "" : key;
};

export const filterCpapFollowupRecords = ({ records = [], currentUser, adminData, nodes = [] }) => {
  if (!isCpapFollowupNodeCustomerScopedUser(currentUser)) return records;
  if (!hasValidCpapCustomerAccess(adminData)) return [];
  return filterRecordsByStaffCustomerAccess({
    records,
    // Other roles must not bypass the CPAP restriction when admin/staff is
    // present. Leave the global helper's behavior for other modules intact.
    currentUser: { roles: ["admin"] },
    adminData,
    nodes,
    getNodeValue: (record) => getCpapFollowupRecordValue(record, "node"),
    getCustomerValue: (record) => {
      const code = resolveNodeCode(nodes, getCpapFollowupRecordValue(record, "node"));
      return resolveCustomerId(getCpapFollowupRecordValue(record, "customer"), nodes.find((node) => node.nodecode === code));
    },
  });
};

export const filterCpapFollowupCustomers = ({ customers, currentUser, adminData, node }) => {
  if (!isCpapFollowupNodeCustomerScopedUser(currentUser)) return customers;
  return filterCpapFollowupRecords({
    records: customers.map((customer) => ({ node: node.nodecode, customer: customer.id, original: customer })),
    currentUser, adminData, nodes: [node],
  }).map((record) => record.original);
};

export const getCpapFollowupAssignedNodes = (nodes, adminData, currentUser) => {
  if (!hasValidCpapCustomerAccess(adminData)) return [];
  const codes = getAssignedStaffNodeCodes(adminData, nodes);
  return nodes.filter((node) => codes.includes(node.nodecode) && (
    !isCpapFollowupNodeCustomerScopedUser(currentUser) || getNodeCustomerAccess(adminData, node.nodecode).mode !== "none"
  ));
};

// A super admin must not inherit node/physician restrictions from an earlier
// entry in the claims array. Keep existing role selection for other accounts.
export const getCpapFollowupRole = (currentUser) => {
  const roles = getCpapFollowupRoles(currentUser);
  return roles.includes("super_admin") ? "super_admin" : roles[0] || "";
};

// Staff may view follow-ups for their assigned node(s). If an account also
// carries a managing role, retain that higher role's existing permissions.
export const isStaffCpapFollowupViewer = (currentUser) => {
  const roles = getCpapFollowupRoles(currentUser);

  return (
    roles.includes("staff") &&
    !roles.some((role) => MANAGING_FOLLOWUP_ROLES.includes(role))
  );
};
